'use client';

import { useState, useCallback, useMemo } from 'react';
import { AlertModal } from '@/components/shared/modals';
import { CancelButton, CTAButton } from '@/components/ui/buttons';
import {
  applyCsvMapping,
  extractCsvSample,
  isPeopleListWithoutContactMethod,
  findCsvAttributeTokenCollisions,
  isBlockingCsvAttributeTokenCollision,
  PERSON_STANDARD_FIELDS,
  COMPANY_STANDARD_FIELDS,
} from '@/utils/csvAiMapping';
import { uploadCsvForAudience } from '@/services/audiences';
import appLogger from '@/utils/logger';
import { toast } from 'react-toastify';
import { useAnalytics } from '@/hooks/useAnalytics';
import { AUDIENCE_CSV_UPLOADED } from '@/lib/analytics-events';
import type { ColumnDefinition } from '@/components/shared/DataTable/types';
import type { DataTableTheme } from '@/components/shared/DataTable/types';
import { AUDIENCE_CSV_IMPORT_TYPE, AUDIENCE_CSV_TEMPLATE } from '@/constants/audiences';
import { useCsvAiMapping } from './hooks/useCsvAiMapping';
import { StepIndicator } from './csv-upload/StepIndicator';
import { UploadStep } from './csv-upload/UploadStep';
import { AnalyzingStep } from './csv-upload/AnalyzingStep';
import { ReviewStep } from './csv-upload/ReviewStep';

const MAX_CSV_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const MAX_CSV_FILE_SIZE_LABEL = '50 MB';

// Static theme object — no useMemo allocation needed since value never changes.
const CSV_PREVIEW_THEME: Partial<DataTableTheme> = {
  typography: { primary: { weight: '400' } },
} as Partial<DataTableTheme>;

function downloadCsvTemplate(type: 'person' | 'company') {
  const headers = AUDIENCE_CSV_TEMPLATE[type].columns.map((c) => c.name).join(',') + '\n';
  const blob = new Blob([headers], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${type}_audience_template.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface CsvUploadWithMappingModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onBack: () => void;
  onUploadSuccess: (data: { list_id: number; type: 'person' | 'company' }) => void;
  existingAudience?: {
    audienceId: number;
    listId: number;
    type: 'person' | 'company';
    name: string;
    rowCount?: number;
  };
}

type Step = 'upload' | 'analyzing' | 'review';

export const CsvUploadWithMappingModal: React.FC<CsvUploadWithMappingModalProps> = ({
  open = false,
  onOpenChange,
  onBack,
  onUploadSuccess,
  existingAudience,
}) => {
  const { capture } = useAnalytics();

  const [currentStep, setCurrentStep] = useState<Step>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvContent, setCsvContent] = useState('');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvSampleRows, setCsvSampleRows] = useState<string[][]>([]);
  const [isEditingMappings, setIsEditingMappings] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tracks whether the user clicked Next. The AI analysis starts on file drop
  // (in parallel with preview render), so the result is often ready by then.
  const [hasRequestedReview, setHasRequestedReview] = useState(false);

  const { aiMapping, setAiMapping, editedMappings, setEditedMappings, isAnalyzing, kickAnalysis, resetMapping } =
    useCsvAiMapping({
      onReady: () => {
        setHasRequestedReview((prev) => {
          if (prev) setCurrentStep('review');
          return prev;
        });
      },
      onError: () => {
        setIsEditingMappings(true);
        setHasRequestedReview((prev) => {
          if (prev) {
            setCurrentStep('review');
            setError('Automatic analysis could not complete. Please map the fields manually below.');
          }
          return prev;
        });
      },
    });

  const resetAll = useCallback(() => {
    setCurrentStep('upload');
    setSelectedFile(null);
    setCsvContent('');
    setCsvHeaders([]);
    setCsvSampleRows([]);
    resetMapping();
    setIsEditingMappings(false);
    setError(null);
    setIsDragging(false);
    setHasRequestedReview(false);
  }, [resetMapping]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) resetAll();
      onOpenChange?.(newOpen);
    },
    [onOpenChange, resetAll],
  );

  const processSelectedFile = useCallback(
    async (file: File) => {
      if (!(file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv'))) {
        setError('Please select a valid CSV file');
        return;
      }
      if (file.size > MAX_CSV_FILE_SIZE_BYTES) {
        setError(`File is too large. Maximum size is ${MAX_CSV_FILE_SIZE_LABEL}.`);
        return;
      }

      setSelectedFile(file);
      setError(null);
      resetMapping();
      setHasRequestedReview(false);

      try {
        const content = await file.text();
        setCsvContent(content);
        const { headers, sampleRows } = extractCsvSample(content);
        setCsvHeaders(headers);
        setCsvSampleRows(sampleRows);
        // Appending to an existing audience locks the type so the AI can't mis-route rows.
        kickAnalysis(headers, sampleRows, file.name, existingAudience?.type);
      } catch (err) {
        appLogger.error('Failed to read CSV file:', { data: err });
        setError("Failed to read CSV file. Please ensure it's a valid CSV.");
      }
    },
    [kickAnalysis, resetMapping, existingAudience],
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }, []);
  const handleDragOver  = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); }, []);
  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const { files } = e.dataTransfer;
    if (files && files.length > 0) await processSelectedFile(files[0]);
  }, [processSelectedFile]);
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) await processSelectedFile(files[0]);
    e.target.value = '';
  }, [processSelectedFile]);
  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null); setCsvContent(''); setCsvHeaders([]); setCsvSampleRows([]);
    resetMapping(); setHasRequestedReview(false); setError(null);
  }, [resetMapping]);

  const handleNextStep = () => {
    if (!selectedFile) return;
    setError(null);
    setHasRequestedReview(true);
    if (aiMapping) { setCurrentStep('review'); return; }
    setCurrentStep('analyzing');
    // Safety net if kick didn't fire during file selection
    if (!isAnalyzing) kickAnalysis(csvHeaders, csvSampleRows, selectedFile.name, existingAudience?.type);
  };

  const handleConfirmMapping = useCallback(async () => {
    if (!aiMapping) return;
    const blockingCollisions = findCsvAttributeTokenCollisions(
      csvHeaders,
      editedMappings,
    ).filter(isBlockingCsvAttributeTokenCollision);
    if (blockingCollisions.length > 0) {
      setCurrentStep('review');
      setIsEditingMappings(true);
      setError(null);
      return;
    }
    try {
      setCurrentStep('analyzing');
      setError(null);

      const finalMapping = { ...aiMapping, field_mappings: editedMappings };
      const transformedResult = applyCsvMapping(csvContent, finalMapping);

      if (transformedResult.data.length === 0) {
        setCurrentStep('review');
        setIsEditingMappings(true);
        setError(
          transformedResult.type === 'person'
            ? 'No rows could be imported. Every row is missing an email, phone, and LinkedIn URL after mapping — check that at least one of those columns is mapped, and that the file has data rows beneath the header.'
            : 'No rows could be imported. Every row is missing a company name after mapping — check that your Company Name column is mapped.',
        );
        return;
      }

      // Defense-in-depth: never append rows to an audience of a different type. The
      // backend picks the destination table purely from the type we send, with no type
      // guard, so a mismatch would write rows into the wrong table (people vs company)
      // under this list_id and silently orphan them. Detection is already locked to the
      // existing type (see kickAnalysis forcedType), so this should never fire — but if it
      // somehow does, surface it instead of losing data.
      if (existingAudience && transformedResult.type !== existingAudience.type) {
        setCurrentStep('review');
        setIsEditingMappings(true);
        setError(
          `This file looks like ${transformedResult.type} data, but you're adding to a ${existingAudience.type} audience. Upload a ${existingAudience.type} CSV, or create a new audience instead.`,
        );
        return;
      }

      const importType =
        transformedResult.type === 'person'
          ? AUDIENCE_CSV_IMPORT_TYPE.PERSON
          : AUDIENCE_CSV_IMPORT_TYPE.COMPANY;

      // applyCsvMapping falls back to company-type when a "person" file has no row
      // with any contact method — a target-account/persona list awaiting enrichment.
      // Surface that reinterpretation so the switch from what Review showed isn't a
      // silent surprise, and point at the tool that completes the list.
      // Skip it when the review step already showed the amber banner for this same
      // outcome — saying it twice reads as two different problems. The toast still
      // covers what the banner cannot: appending to an existing audience, where the
      // banner is deliberately suppressed.
      const warnedDuringReview = !existingAudience
        && isPeopleListWithoutContactMethod(transformedResult);
      if (transformedResult.type !== finalMapping.detected_type && !warnedDuringReview) {
        toast.info(
          'No contact info was found in this file, so it imported as companies. Use "Find People" on the audience page to discover contacts for these accounts.',
          { autoClose: 10000, position: 'top-right' },
        );
      }

      appLogger.info('CSV mapping applied:', {
        data: {
          file: selectedFile?.name,
          type: transformedResult.type,
          rowCount: transformedResult.data.length,
          isUpdate: !!existingAudience,
        },
      });

      const uploadResult = await uploadCsvForAudience({
        type: importType,
        data: transformedResult.data,
        template:
          importType === AUDIENCE_CSV_IMPORT_TYPE.PERSON
            ? AUDIENCE_CSV_TEMPLATE[AUDIENCE_CSV_IMPORT_TYPE.PERSON]
            : AUDIENCE_CSV_TEMPLATE[AUDIENCE_CSV_IMPORT_TYPE.COMPANY],
        list_id: existingAudience?.listId,
        audienceId: existingAudience?.audienceId,
      });

      capture(AUDIENCE_CSV_UPLOADED, { row_count: transformedResult.data.length });
      handleOpenChange(false);
      onUploadSuccess({ list_id: uploadResult.list_id, type: importType });
    } catch (err: unknown) {
      appLogger.error('Failed to upload CSV:', { data: err });

      const responseData = (err as any)?.response?.data;
      const apiMessage =
        responseData?.message || (err instanceof Error ? err.message : 'Failed to upload CSV data. Please try again.');

      let userMessage = apiMessage;
      if (apiMessage?.includes('violates not-null constraint') || apiMessage?.includes('null value in column')) {
        const fieldMatch = apiMessage.match(/column "(\w+)"/);
        const field = fieldMatch?.[1] ?? 'required field';
        userMessage = `Some records are missing the required "${field}" field. Please check your CSV data and try again.`;
      }

      toast.error(userMessage, { autoClose: 8000, position: 'top-right' });
      setError(userMessage);
      setCurrentStep('review');
      setIsEditingMappings(false);
    }
  }, [aiMapping, csvContent, csvHeaders, editedMappings, existingAudience, selectedFile, capture, handleOpenChange, onUploadSuccess]);

  const handleMappingChange = (col: string, field: string) => {
    setError(null);
    setEditedMappings((prev) => {
      const next = { ...prev };
      if (field === '__SKIP__') delete next[col];
      else next[col] = field;
      return next;
    });
  };

  const handleSaveEdits = () => {
    // Persist edits into aiMapping so "Discard" reverts to this saved state.
    if (aiMapping) setAiMapping({ ...aiMapping, field_mappings: editedMappings });
    setIsEditingMappings(false);
  };

  const handleCancelEdits = () => {
    setEditedMappings(aiMapping?.field_mappings ?? {});
    setIsEditingMappings(false);
  };

  const handleBackToUpload = () => {
    // Keep the computed mapping — selecting a different file resets it via
    // processSelectedFile/handleRemoveFile, so going Back → Next on the SAME file
    // reuses the existing analysis instead of firing a redundant LLM call.
    setCurrentStep('upload');
    setIsEditingMappings(false);
    setError(null);
  };

  const standardFields =
    aiMapping?.detected_type === 'person' ? PERSON_STANDARD_FIELDS : COMPANY_STANDARD_FIELDS;
  const attributeTokenCollisions = useMemo(
    () => findCsvAttributeTokenCollisions(csvHeaders, editedMappings),
    [csvHeaders, editedMappings],
  );

  // Run the real transform during review so the step can warn about the outcome before
  // the user commits to it, rather than only explaining it in a toast afterwards. This is
  // the same call the confirm handler makes, so the warning cannot disagree with the import.
  // Appending is excluded: there the type is fixed by the destination audience, not by
  // the file, so there is nothing to warn about.
  const predictedImport = useMemo(() => {
    // Only on the review step. The AI analysis is kicked at file-drop, so without this the
    // transform would run while the user is still looking at the upload preview — a
    // second-long freeze on a large file, for a result nothing on screen is using yet.
    if (currentStep !== 'review' || !aiMapping || !csvContent || existingAudience) return null;
    try {
      return applyCsvMapping(csvContent, { ...aiMapping, field_mappings: editedMappings });
    } catch {
      // A malformed file surfaces its own error on confirm; stay quiet rather than guess.
      return null;
    }
  }, [currentStep, aiMapping, csvContent, editedMappings, existingAudience]);

  const csvTableData = useMemo(
    () =>
      csvSampleRows.slice(0, 10).map((row, idx) => {
        const rowData: Record<string, string | number> = { _id: idx };
        csvHeaders.forEach((h, hi) => { rowData[h] = row[hi] || '—'; });
        return rowData;
      }),
    [csvHeaders, csvSampleRows],
  );

  const csvTableColumns = useMemo(
    (): ColumnDefinition[] =>
      csvHeaders.map((h) => ({ id: h, label: h, accessor: h, type: 'text' as const, sortable: false })),
    [csvHeaders],
  );

  const getTitle = () => {
    if (currentStep === 'analyzing') return aiMapping ? 'Uploading...' : 'Analyzing CSV...';
    if (currentStep === 'review') return 'Review Field Mapping';
    if (existingAudience) return `Add contacts to "${existingAudience.name}"`;
    return 'Import from CSV';
  };

  const renderFooter = () => {
    if (currentStep === 'upload') {
      return (
        <div className="flex justify-between items-center gap-2 w-full">
          <CancelButton onClick={() => { handleOpenChange(false); onBack(); }}>
            Cancel
          </CancelButton>
          {csvHeaders.length > 0 && (
            <p className="text-xs text-slate-500 flex-1 text-center">
              Preview showing {Math.min(csvSampleRows.length, 10)} of {csvSampleRows.length} rows
            </p>
          )}
          <CTAButton onClick={handleNextStep} disabled={!selectedFile}>
            Next
          </CTAButton>
        </div>
      );
    }

    if (currentStep === 'review') {
      return (
        <div className="flex justify-between gap-2 w-full">
          <CancelButton onClick={handleBackToUpload}>Back to Upload</CancelButton>
          {isEditingMappings ? (
            // "Save & Continue" lets users confirm in one click without a separate Save step
            <CTAButton onClick={handleConfirmMapping}>Save &amp; Continue</CTAButton>
          ) : (
            <CTAButton onClick={handleConfirmMapping}>Confirm &amp; Continue</CTAButton>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <AlertModal
      open={open}
      onOpenChange={handleOpenChange}
      title={getTitle()}
      size="4xl"
      height="xl"
      className="max-h-[80vh]"
      footer={renderFooter()}
    >
      {currentStep !== 'analyzing' && (
        <div className="mb-5">
          <StepIndicator currentStep={currentStep} />
        </div>
      )}

      {currentStep === 'upload' && (
        <UploadStep
          isDragging={isDragging}
          selectedFile={selectedFile}
          csvHeaders={csvHeaders}
          csvSampleRows={csvSampleRows}
          csvTableData={csvTableData}
          csvTableColumns={csvTableColumns}
          csvPreviewTheme={CSV_PREVIEW_THEME}
          isAnalyzing={isAnalyzing}
          error={error}
          existingAudience={existingAudience}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onFileSelect={handleFileSelect}
          onRemoveFile={handleRemoveFile}
          onBack={() => { handleOpenChange(false); onBack(); }}
          onDownloadTemplate={downloadCsvTemplate}
        />
      )}

      {currentStep === 'analyzing' && <AnalyzingStep aiMapping={aiMapping} />}

      {currentStep === 'review' && aiMapping && (
        <ReviewStep
          csvHeaders={csvHeaders}
          csvSampleRows={csvSampleRows}
          editedMappings={editedMappings}
          aiMapping={aiMapping}
          standardFields={standardFields}
          isEditingMappings={isEditingMappings}
          error={error}
          attributeTokenCollisions={attributeTokenCollisions}
          predictedImport={predictedImport}
          existingAudience={existingAudience}
          csvPreviewTheme={CSV_PREVIEW_THEME}
          onMappingChange={handleMappingChange}
          onStartEditing={() => setIsEditingMappings(true)}
          onSaveEdits={handleSaveEdits}
          onCancelEdits={handleCancelEdits}
        />
      )}
    </AlertModal>
  );
};
